/**
 * Pure view logic for the Bexio "Rechnungstexte" section (D4.1a).
 *
 * WHY ITS OWN MODULE, NOT `bexioSettingsView.js`. That file is at ~547 lines and
 * already carries the provider-config form logic. These helpers belong to a
 * different persistence domain — `organization_billing_settings`, reached through
 * the billing-settings endpoint, with no Bexio provider call anywhere on the path.
 * Keeping them apart is what makes "this is not provider config" checkable rather
 * than merely asserted (CLAUDE.md Regel 48).
 *
 * WHAT THIS DOMAIN IS. `default_header_text` is stored on the org's billing-settings
 * row and is read by exactly one consumer: the Bexio draft payload builder. That is
 * why the EDIT surface lives under Bexio › Einstellungen even though the STORAGE did
 * not move. `default_footer_text` sits in the same row but is read by four document
 * types (Bexio invoice, native invoice PDF, handover protocol, key receipt), so it
 * stays editable on the cross-document billing-settings page and is only DISPLAYED
 * here. There is no footer helper in this module on purpose — a `isFooterDirty`
 * would be the first step toward a second footer edit surface.
 */

/**
 * Normalize a letterhead value for comparison ONLY.
 *
 * `null` (never set) and `""` (typed, then cleared) are the same downstream: both
 * `load_org_header_text` and the payload resolver treat blank as unset and omit the
 * `header` key entirely. So they must not read as a pending change.
 *
 * Deliberately NOT trimmed. The backend's `_strip_short_text` validator omits both
 * letterhead fields for the same reason: internal whitespace and newlines ARE the
 * formatting, and each newline becomes a `<br />` on the Bexio wire. Trimming here
 * would make " Text" look identical to "Text" and silently discard the operator's
 * leading blank line on save.
 */
export function normalizeLetterheadText(value) {
  return value == null ? "" : String(value);
}

/** True when the draft differs from what is stored. See the null/"" note above. */
export function isHeaderDirty(draft, persisted) {
  return normalizeLetterheadText(draft) !== normalizeLetterheadText(persisted);
}

/**
 * The value that goes on the wire for `default_header_text`.
 *
 * Returns the raw string, empty string included. This is byte-for-byte what the
 * billing-settings page sent before D4.1a moved the control: its textarea bound
 * `value={form.default_header_text || ""}`, so a cleared field reached the PATCH as
 * `""`, never as `null`. Sending `null` instead would be a NEW clearing semantic
 * introduced by a slice whose whole point is that nothing but the edit surface moves.
 */
export function headerPatchValue(draft) {
  return normalizeLetterheadText(draft);
}

/**
 * What to tell the operator when saving the header fails.
 *
 * Status-driven, and deliberately free of any Bexio-connection wording. This save
 * goes to `PATCH /api/admin/billing-settings`; it builds no client, reads no token
 * and reaches no provider. A "Bexio-Verbindung ist nicht aktiv." message here would
 * be a lie in every case and would send the operator to fix the wrong thing.
 */
export function headerSaveErrorMessage(error) {
  const status = error?.status;
  if (status === 403) return "Keine Berechtigung zum Speichern.";
  if (status === 422) {
    return error?.message || "Die Kopfzeile wurde nicht akzeptiert.";
  }
  return "Die Kopfzeile konnte nicht gespeichert werden.";
}
